import { useEffect, useRef, useState, useCallback } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { BIOMETRIC_LOCK_FEATURE_ENABLED } from '@/config/constants';

const PREF_KEY = 'ocopro_biometric_lock';
const BG_FLAG = 'ocopro_was_background';

// Vrai dès qu'on a effectué la vérification "premier démarrage à froid" dans cette
// session JS. Remis à false uniquement par un nouveau démarrage de processus
// (variable module — ne survit pas à un cold start).
let _coldStartLockDone = false;

export function getBiometricLockEnabled(): boolean {
  return localStorage.getItem(PREF_KEY) === 'true';
}

export function setBiometricLockEnabled(enabled: boolean): void {
  localStorage.setItem(PREF_KEY, enabled ? 'true' : 'false');
}

export function useBiometricLock(isAuthenticated: boolean) {
  const [isLocked, setIsLocked] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  // Ref locale pour détecter background→foreground dans le même cycle de vie
  const wasInBackground = useRef(false);
  // Miroir synchrone de isAuthenticating, lu par le listener appStateChange (voir
  // plus bas) — un state React ne serait pas à jour dans la closure du listener.
  const isAuthenticatingRef = useRef(false);

  const authenticate = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setIsLocked(false);
      return;
    }
    setIsAuthenticating(true);
    isAuthenticatingRef.current = true;
    try {
      const { NativeBiometric } = await import('capacitor-native-biometric');
      const available = await NativeBiometric.isAvailable({ useFallback: true });
      if (!available.isAvailable) {
        setIsLocked(false);
        return;
      }
      await NativeBiometric.verifyIdentity({
        reason: "Vérifiez votre identité pour accéder à O'Copro",
        title: "O'Copro",
        negativeButtonText: 'Annuler',
        useFallback: true,
        maxAttempts: 5,
      });
      setIsLocked(false);
      wasInBackground.current = false;
      localStorage.removeItem(BG_FLAG);
    } catch {
      // Auth échouée ou annulée — l'overlay reste affiché
    } finally {
      setIsAuthenticating(false);
      isAuthenticatingRef.current = false;
    }
  }, []);

  // Vérifier au montage :
  //  1. Démarrage à froid (nouveau processus) → verrouiller systématiquement si activé,
  //     peu importe si l'app était en arrière-plan ou premier plan avant d'être tuée.
  //  2. Recréation d'activité sans nouveau processus (ex: tap notification) → vérifier BG_FLAG.
  useEffect(() => {
    if (!BIOMETRIC_LOCK_FEATURE_ENABLED || !isAuthenticated) return;
    if (!Capacitor.isNativePlatform()) return;

    if (!_coldStartLockDone) {
      // Premier passage dans cette session JS = cold start ou recréation de processus
      _coldStartLockDone = true;
      if (getBiometricLockEnabled()) {
        setIsLocked(true);
        return;
      }
    }

    // Recréation d'activité dans le même processus (BG_FLAG posé par appStateChange)
    if (localStorage.getItem(BG_FLAG) === '1' && getBiometricLockEnabled()) {
      setIsLocked(true);
    }
  }, [isAuthenticated]);

  // Écouter les transitions avant-plan / arrière-plan
  useEffect(() => {
    if (!BIOMETRIC_LOCK_FEATURE_ENABLED || !isAuthenticated) return;

    const listenerPromise = CapApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      // Sur iOS, l'invite système Face ID/code (LAContext) déclenche elle-même un
      // resignActive/becomeActive, comme une vraie mise en arrière-plan de l'app —
      // ignorer ces transitions tant qu'une authentification est en cours, sinon la
      // fermeture de l'invite après un code correct reverrouille l'app aussitôt
      // (Android ne bascule pas l'app par son BiometricPrompt, pas de souci ici).
      if (isAuthenticatingRef.current) return;

      if (!isActive) {
        // Mise en arrière-plan — persister dans localStorage (survit à un relancement
        // d'activité ET à une fermeture complète du processus, contrairement à sessionStorage)
        wasInBackground.current = true;
        localStorage.setItem(BG_FLAG, '1');
      } else if (wasInBackground.current || localStorage.getItem(BG_FLAG) === '1') {
        wasInBackground.current = false;
        if (getBiometricLockEnabled()) {
          setIsLocked(true);
        }
      }
    });

    return () => {
      listenerPromise.then((l: { remove: () => void }) => l.remove());
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLocked && !isAuthenticating) {
      authenticate();
    }
  }, [isLocked, isAuthenticating, authenticate]);

  return { isLocked, isAuthenticating, authenticate };
}
